import { useEffect, useRef, useState } from "react";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Google Pay button — only renders when the browser confirms the user is
// signed into a Google account AND has a saved payment method
// (`isReadyToPay.result === true && paymentMethodPresent === true`).
// On click, Google returns a Stripe-compatible single-use token which we
// forward to the backend; the backend creates + confirms a PaymentIntent.
export default function GooglePayButton({
  lookupKey,
  customAmountCents,
  amountCents,
  name,
  message,
  onPaid,
  onError,
  onFallback,
}) {
  const hostRef = useRef(null);
  const clientRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  // Live Stripe keys + Google's TEST environment = test tokens that Stripe
  // rejects. Until a Google merchant ID is set, send people to Stripe's own
  // checkout, which offers Google Pay and cards itself.
  const liveWithoutMerchant = config?.mode === "live" && config?.google_pay_env !== "PRODUCTION";
  // Why the button isn't visible — surfaced to the user via a tiny hint
  // so they aren't left wondering why "Pay with G Pay" is missing.
  const [reason, setReason] = useState("Loading Google Pay…");

  // Fetch the publishable config from our backend once per mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/payments/google-pay/config");
        if (!cancelled) setConfig(data);
      } catch (err) {
        console.warn("[gpay] config fetch failed:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Boot the Google Pay client. We poll for `window.google.payments.api`
  // because `pay.js` is loaded `async` in index.html so it may not be
  // ready at mount time.
  useEffect(() => {
    if (!config || !config.publishable_key) return;
    let cancelled = false;

    const bootstrap = async () => {
      const g = window.google;
      if (!g?.payments?.api || !hostRef.current) {
        setReason("Google Pay SDK didn't load (maybe blocked by an ad-blocker?)");
        return;
      }
      const client = new g.payments.api.PaymentsClient({
        environment: config.google_pay_env || "TEST",
      });
      clientRef.current = client;
      try {
        const result = await client.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [{
            type: "CARD",
            parameters: {
              allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
              allowedCardNetworks: ["AMEX", "DISCOVER", "INTERAC", "JCB", "MASTERCARD", "VISA"],
            },
          }],
          // Previously we required `existingPaymentMethodRequired: true`
          // which hid the button for anyone without a saved card. Dropping
          // it to `false` means Google shows the button to every signed-in
          // user and, on click, lets them add a card inline. Massively
          // more discoverable without changing the actual charge flow.
          existingPaymentMethodRequired: false,
        });
        if (cancelled || !hostRef.current) return;
        if (result?.result !== true) {
          setReason("Google Pay isn't available on this browser / device");
          return;
        }
        hostRef.current.replaceChildren();
        const button = client.createButton({
          onClick: () => pay(),
          buttonColor: "black",
          buttonType: "pay",
          buttonSizeMode: "fill",
        });
        hostRef.current.appendChild(button);
        setReady(true);
        setReason("");
      } catch (err) {
        console.warn("[gpay] isReadyToPay failed:", err?.message || err);
        if (!cancelled) {
          setReady(false);
          setReason("Google Pay check failed — try again later");
        }
      }
    };

    const timer = window.setInterval(() => {
      if (window.google?.payments?.api) {
        window.clearInterval(timer);
        bootstrap();
      }
    }, 60);
    // Give pay.js up to 10 s to arrive — if it's blocked by an ad-blocker
    // or offline, we just never render (that's the spec).
    const giveUp = window.setTimeout(() => {
      window.clearInterval(timer);
      if (!window.google?.payments?.api) setReason("Google Pay couldn't load here — use the card button");
    }, 10000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.clearTimeout(giveUp);
      if (hostRef.current) hostRef.current.replaceChildren();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const pay = async () => {
    const client = clientRef.current;
    if (!client || busy || !config) return;
    const totalPence = Number(amountCents || 0);
    if (!totalPence || totalPence <= 0) {
      toast.error("Please pick an amount first");
      return;
    }
    setBusy(true);
    try {
      // `loadPaymentData` MUST run synchronously from the Google-created
      // button's click handler, so no awaits before this call.
      const data = await client.loadPaymentData({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [{
          type: "CARD",
          parameters: {
            allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
            allowedCardNetworks: ["AMEX", "DISCOVER", "INTERAC", "JCB", "MASTERCARD", "VISA"],
          },
          tokenizationSpecification: {
            type: "PAYMENT_GATEWAY",
            parameters: {
              gateway: "stripe",
              "stripe:version": "2020-08-27",
              "stripe:publishableKey": config.publishable_key,
            },
          },
        }],
        emailRequired: true,
        merchantInfo: {
          merchantName: "Mango Notes",
          ...(config.google_pay_env === "PRODUCTION" && config.google_pay_merchant_id
            ? { merchantId: config.google_pay_merchant_id }
            : {}),
        },
        transactionInfo: {
          countryCode: config.country || "GB",
          currencyCode: (config.currency || "gbp").toUpperCase(),
          totalPriceStatus: "FINAL",
          totalPrice: (totalPence / 100).toFixed(2),
        },
      });
      const token = data?.paymentMethodData?.tokenizationData?.token;
      if (!token) throw new Error("Google Pay returned no payment token");

      const payload = { token, name, message, email: data?.email || undefined };
      if (customAmountCents != null) payload.custom_amount_cents = Number(customAmountCents);
      else if (lookupKey) payload.lookup_key = lookupKey;

      const { data: res } = await api.post("/payments/google-pay", payload);
      if (res?.status === "succeeded") {
        
        onPaid?.(res);
      } else if (res?.status === "requires_action") {
        toast.error("Payment needs extra verification — please use the Stripe button");
      } else {
        toast.error(`Payment ${res?.status || "failed"}`);
      }
    } catch (err) {
      // CANCELED is the user dismissing the sheet — stay silent.
      if (err?.statusCode === "CANCELED") return;
      const msg = err?.response?.data?.detail || err?.message || "Payment failed";
      console.warn("[gpay] pay failed:", msg);
      toast.error(msg);
      onError?.(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full space-y-1.5">
      <div
        ref={hostRef}
        data-testid="google-pay-host"
        className={ready && !liveWithoutMerchant ? "block w-full [&_button]:w-full" : "hidden"}
        aria-busy={busy ? "true" : "false"}
      />
      {(!ready || liveWithoutMerchant) && config && onFallback && (
        <button
          type="button"
          onClick={onFallback}
          disabled={!amountCents}
          className="w-full h-11 rounded-full bg-black text-white text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-stone-800 disabled:opacity-50 transition-colors"
          data-testid="google-pay-card-fallback"
        >
          <CreditCard className="h-4 w-4" />
          {liveWithoutMerchant ? "Google Pay or card" : "Pay with card"}
        </button>
      )}
      {!ready && reason && !liveWithoutMerchant && (
        <p
          className="text-[11px] text-stone-500 dark:text-stone-400 text-center px-2 italic"
          data-testid="google-pay-fallback"
        >
          {reason}
        </p>
      )}
    </div>
  );
}
